<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { useModal } from 'vue-final-modal';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import CreateItems from '~/terra/components/create-shortcut.vue';
import MapToolIcons from '~/terra/components/map-tool-icons.vue';
import FeatureSearchPopup from '~/terra/molecules/feature-search-popup.vue';
import GalleryView from '~/terra/molecules/gallery/gallery-view.vue';
import DefectGalleryViewDetails from '~/therm/components/defect-details/defect-gallery-view-details.vue';
import ThermFeatureDetails from '~/therm/components/therm-feature-details.vue';
import { useThermTaskFilters } from '~/therm/composables/therm-filters.js';
import { useThermStore } from '../store/therm.store';

defineProps({
  active_tool: {
    type: String,
    default: '',
  },
  active_tools: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Array,
    default: () => [],
  },

});
const emit = defineEmits(['toolActivated', 'createTask', 'createForm']);
const { $t } = useCommonImports();
const therm_store = useThermStore();
const auth_store = useAuthStore();
function activateTool(tool) {
  emit('toolActivated', tool.event);
  emit(tool.event);
}

const zoom_tools = computed(() => {
  return [
    {
      tooltip: $t('Zoom in'),
      icon: IconHawkPlus,
      event: 'zoom-in',
      is_size: 'w-[20px] h-[20px]',
      action: () => activateTool({ event: 'zoom-in' }),
    },
    {
      tooltip: $t('Zoom out'),
      icon: IconHawkMinus,
      event: 'zoom-out',
      is_size: 'w-[20px] h-[20px]',
      action: () => activateTool({ event: 'zoom-out' }),
    },
    {
      tooltip: $t('Reset'),
      icon: IconHawkRefreshCcwOne,
      event: 'reset-north',
      is_size: 'w-[20px] h-[20px]',
      action: () => activateTool({ event: 'reset-north' }),
    },
  ];
});

const creation_tool = computed(() => {
  return (auth_store.has_tasks_access || auth_store.has_forms_access)
    ? [
        {
          tooltip: $t('Create'),
          icon: IconHawkPoint,
          event: 'create',
          tooltip: therm_store.selected_features?.length > 500 ? $t('Can not create more than 500 tasks at once') : null,
          action: () => {
            activateTool({ event: 'create' });
          },
        },
      ]
    : [];
});

const filter_tools = computed(() => {
  return [
    {
      tooltip: $t('Search'),
      icon: IconHawkSearch,
      event: 'search',
      action: () => {
        const { open: openFeatureSearchModal, close: closeFeatureSearchModal } = useModal({
          component: FeatureSearchPopup,
          attrs: {
            module: 'therm',
            onClose() {
              closeFeatureSearchModal();
            },
          },
        });
        openFeatureSearchModal();
      },
    },
    {
      tooltip: $t('Filter defects'),
      icon: IconHawkFilter,
      event: 'filters',
      action: () => useThermTaskFilters(),
      is_active: (() => {
        const f = therm_store.filters;
        if (!f || !Object.keys(f).length)
          return false;

        const has_non_default_temp = f.temperature_difference && (f.temperature_difference[0] !== 0 || f.temperature_difference[1] !== 100);
        const has_non_default_types = f.include_feature_types && !(f.include_feature_types.length === 1 && f.include_feature_types[0] === 'all');
        const has_non_default_filter = f.filter && (f.filter.show_defects !== true || f.filter.associated_with_defects || f.filter.not_associated_with_defects || f.filter.history);
        const has_other_filters = Object.keys(f).some(key => !['temperature_difference', 'include_feature_types', 'filter', 'temp_diff_elements'].includes(key));

        return has_non_default_temp || has_non_default_types || has_non_default_filter || has_other_filters;
      })(),
    },
    {
      tooltip: $t('Compare'),
      icon: IconHawkCompare,
      event: 'compare',
      action: () => {
        activateTool({ event: 'compare' });
      },
    },
  ];
});
</script>

<template>
  <div>
    <div class="absolute left-3 top-3">
      <MapToolIcons
        style="position: relative"
        :tools="zoom_tools"
        :active_tool="active_tool"
        :disabled="disabled"
        :active_tools="active_tools"
      />
    </div>
    <div class="absolute right-4 top-3 flex">
      <GalleryView
        :store="therm_store"
        is_therm_viewer
      >
        <template #feature_details>
          <DefectGalleryViewDetails />
        </template>
      </GalleryView>

      <div v-if="creation_tool.length" class="relative flex flex-col  mr-2.5">
        <MapToolIcons
          style="position: relative"
          :tools="creation_tool"
          :disabled="((therm_store.selected_features?.length > 500) ? ['create'] : disabled)"
          :active_tools="active_tools"
          :horizontal_icons="true"
        />
        <CreateItems
          v-if="active_tool === 'create'"
          class="absolute right-0 top-10 z-10"
          @create-task="emit('createTask')"
          @create-form="emit('createForm')"
        />
      </div>
      <MapToolIcons
        style="position: relative"
        :tools="filter_tools"
        :disabled="[]"
        :horizontal_icons="true"
      />
    </div>
    <ThermFeatureDetails />
  </div>
</template>
